import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Icon } from "../../kit";
import { notice, useBindingCtx, useDomainState, useEditorStore, useUi } from "../../../state/editor/useEditor";
import type { UiState } from "../../../state/editor/ui";
import { PINNED_PANEL, switchModePatch } from "../basic/mode";
import { useBasicEngine } from "../basic/useBasicEngine";
import { useConfiguratorDraft } from "../configurator/draft";
import { useSceneFileActions } from "../scene/useSceneFileActions";
import { GROUPS } from "../sections/groups";
import { ALL_GROUP_SECTIONS } from "../sections";
import { CUSTOM_TERMS } from "../sections/searchIndex.gen";
import { FOCUS_SEARCH_EVENT } from "./RightPanel";
import { Marked } from "./rows";
import { splitUnit } from "./rowMeta";
import { expand, markTerms, tokenize } from "./search";

/**
 * Keyboard for the whole studio, and ⌘K.
 *
 *   ⌘Z / Ctrl+Z            Undo — any change, Basic or Advanced
 *   ⇧⌘Z / Ctrl+Shift+Z / Ctrl+Y   Redo
 *   /                      Search Advanced settings
 *   ⌘K / Ctrl+K            Go to any setting or action by name
 *
 * Undo/Redo and "/" stand down while you are typing in a field — there ⌘Z
 * belongs to the text box. The viewer iframe keeps its own keys: a keystroke
 * made with focus inside the canvas never reaches this document.
 */

interface Item {
  id: string;
  title: string;
  where: string;
  icon: string;
  hay: string;
  run: () => void;
}

const isTyping = (t: EventTarget | null) => {
  const el = t as HTMLElement | null;
  if (!el) return false;
  if (el.isContentEditable) return true;
  if (el.tagName === "TEXTAREA" || el.tagName === "SELECT") return true;
  if (el.tagName !== "INPUT") return false;
  const type = (el as HTMLInputElement).type;
  return !["range", "checkbox", "radio", "button", "color", "submit"].includes(type);
};

export function StudioKeys() {
  const [ui, patchUi] = useUi();
  const engine = useBasicEngine();
  const [open, setOpen] = useState(false);
  const uiRef = useRef(ui);
  uiRef.current = ui;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const mod = e.metaKey || e.ctrlKey;
      const k = e.key.toLowerCase();
      // Preview as customer owns the keyboard: Undo would take back the
      // designer's own steps under the shopper's, and Esc there is "Exit".
      if (uiRef.current.customerPreview) return;
      if (mod && k === "k") {
        e.preventDefault();
        setOpen((v) => !v);
        return;
      }
      if (isTyping(e.target) || e.defaultPrevented) return;
      const fail = (err: Error) => notice(`Couldn't do that: ${err.message}`);
      if (mod && !e.altKey && ((k === "z" && e.shiftKey) || (k === "y" && e.ctrlKey && !e.metaKey))) {
        e.preventDefault();
        if (engine.canRedo() && !engine.busy) void engine.redo().catch(fail);
        return;
      }
      if (mod && !e.altKey && !e.shiftKey && k === "z") {
        e.preventDefault();
        if (engine.canUndo() && !engine.busy) void engine.undo().catch(fail);
        return;
      }
      if (e.key === "/" && !mod && !e.altKey) {
        e.preventDefault();
        const cur = uiRef.current;
        const patch: Partial<UiState> =
          cur.inspectorMode === "advanced" ? {} : switchModePatch(cur, "advanced");
        const tab = (patch.rightTab ?? cur.rightTab) as string | null;
        if (!tab || tab === "ai") patch.rightTab = cur.lastAdvancedTab || GROUPS[0].id;
        patchUi(patch);
        // The panel may be mounting; focus once it has.
        window.setTimeout(() => window.dispatchEvent(new Event(FOCUS_SEARCH_EVENT)), 30);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [engine, patchUi]);

  return open ? <Palette onClose={() => setOpen(false)} /> : null;
}

function Palette({ onClose }: { onClose: () => void }) {
  const [ui, patchUi] = useUi();
  const engine = useBasicEngine();
  const store = useEditorStore();
  const ctx = useBindingCtx();
  const [config, draft] = useConfiguratorDraft();
  const hasModel = useDomainState("models").length > 0;
  const { available: sceneFiles, save: saveScene, open: openScene, ringWithScene } = useSceneFileActions();
  const [q, setQ] = useState("");
  const [at, setAt] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);

  const items = useMemo<Item[]>(() => {
    const goTo = (group: string, sectionId: string, rowId: string | null, experimental: boolean) => () => {
      const base = ui.inspectorMode === "advanced" ? {} : switchModePatch(ui, "advanced");
      const section = ALL_GROUP_SECTIONS.find((g) => g.tabId === group)?.sections.find((s) => s.id === sectionId);
      const first = rowId ?? section?.rows[0]?.id ?? null;
      // Per-part settings exist only while a part is selected — say so rather
      // than open a group with nothing where the setting should be.
      if (section?.visible && !section.visible((d) => store.getState(d), ctx)) {
        notice(`${section.displayTitle ?? section.title}: select a part in the view first.`);
      }
      patchUi({
        ...base,
        rightTab: group,
        lastRightTab: group,
        lastAdvancedTab: group,
        search: "",
        collapsed: { ...ui.collapsed, [`${group}.${sectionId}`]: false },
        ...(experimental ? { showExperimental: true } : {}),
        highlight: first ? { rows: [`${group}.${sectionId}.${first}`], at: Date.now() } : null,
      });
    };
    const out: Item[] = [];
    const fail = (err: Error) => notice(`Couldn't do that: ${err.message}`);
    // Actions first in the list when nothing is typed.
    out.push(
      { id: "a:undo", title: engine.undoLabel() ? `Undo ${engine.undoLabel()}` : "Undo", where: "⌘Z", icon: "undo", hay: "undo back", run: () => void engine.undo().catch(fail) },
      { id: "a:redo", title: engine.redoLabel() ? `Redo ${engine.redoLabel()}` : "Redo", where: "⇧⌘Z", icon: "redo", hay: "redo again", run: () => void engine.redo().catch(fail) },
      {
        id: "a:mode",
        title: ui.inspectorMode === "basic" ? "Switch to Advanced" : "Switch to Basic",
        where: "Mode",
        icon: "tune",
        hay: "mode basic advanced switch simple",
        run: () => patchUi(switchModePatch(ui, ui.inspectorMode === "basic" ? "advanced" : "basic")),
      },
      {
        id: "a:export",
        title: "Export…",
        where: "Output",
        icon: "ios_share",
        hay: "export download image video snapshot render",
        run: () => patchUi(ui.inspectorMode === "basic" ? { leftPanel: "output", lastBasicLeftPanel: "output" } : { rightTab: "output", lastRightTab: "output", lastAdvancedTab: "output" }),
      },
      // v65 scene files — only on a viewer that has them (RPC has no timeout).
      ...(sceneFiles
        ? [
            {
              id: "a:scene-save",
              title: "Save scene file",
              where: "Whole setup",
              icon: "save",
              hay: "save scene file setup look whole everything download jscene export settings",
              run: () => void saveScene(),
            },
            {
              id: "a:scene-open",
              title: "Open scene file…",
              where: "Whole setup",
              icon: "folder_open",
              hay: "open scene file setup look whole load import jscene restore settings",
              run: () => void openScene(),
            },
            ...(hasModel
              ? [
                  {
                    id: "a:scene-ring",
                    title: "Save ring + scene",
                    where: "Whole setup",
                    icon: "deployed_code",
                    hay: "ring scene save export 3d file model glb with setup look inside share upload",
                    run: () => void ringWithScene(),
                  },
                ]
              : []),
          ]
        : []),
      { id: "a:present", title: "Present to a customer", where: "Full screen", icon: "slideshow", hay: "present fullscreen customer show", run: () => patchUi({ present: true }) },
      ...(hasModel
        ? [
            {
              id: "a:configurator",
              title: config.on ? "Hide configurator" : "Show configurator",
              where: "Customer configurator",
              icon: "storefront",
              hay: "configurator customer choices variants options shoppers link bar show hide",
              run: () => draft.setOn(!config.on),
            },
            {
              id: "a:customer-preview",
              title: "Preview as customer",
              where: "Customer configurator",
              icon: "visibility",
              hay: "preview customer shopper link configurator see view",
              run: () => patchUi({ customerPreview: true }),
            },
          ]
        : []),
      {
        id: "a:pinned",
        title: "Open Pinned settings",
        where: "Advanced",
        icon: "star",
        hay: "pinned favourites favorites starred",
        run: () => patchUi({ ...(ui.inspectorMode === "advanced" ? {} : switchModePatch(ui, "advanced")), rightTab: PINNED_PANEL, lastRightTab: PINNED_PANEL }),
      },
      {
        id: "a:exp",
        title: ui.showExperimental ? "Hide experimental sections" : "Show experimental sections",
        where: "Advanced",
        icon: "science",
        hay: "experimental debug developer hidden",
        run: () => patchUi({ showExperimental: !ui.showExperimental }),
      },
    );
    for (const g of GROUPS) {
      out.push({
        id: `g:${g.id}`,
        title: `Open ${g.label}`,
        where: "Advanced group",
        icon: g.icon,
        hay: `${g.label} group open`.toLowerCase(),
        run: () => patchUi({ ...(ui.inspectorMode === "advanced" ? {} : switchModePatch(ui, "advanced")), rightTab: g.id, lastRightTab: g.id, lastAdvancedTab: g.id }),
      });
    }
    for (const { tabId, sections } of ALL_GROUP_SECTIONS) {
      const gl = GROUPS.find((g) => g.id === tabId)?.label ?? tabId;
      for (const s of sections) {
        const st = s.displayTitle ?? s.title;
        const exp = !!s.experimental;
        out.push({
          id: `s:${tabId}.${s.id}`,
          title: st,
          where: gl,
          icon: GROUPS.find((g) => g.id === tabId)?.icon ?? "tune",
          hay: `${st} ${s.title} ${gl}`.toLowerCase(),
          run: goTo(tabId, s.id, null, exp),
        });
        for (const r of s.rows) {
          const custom = r.kind === "custom" ? CUSTOM_TERMS[`${s.id}.${r.id}`] ?? [] : [];
          const label = r.label ? splitUnit(r.label).text : "";
          if (label) {
            out.push({
              id: `r:${tabId}.${s.id}.${r.id}`,
              title: label,
              where: `${gl} › ${st}`,
              icon: "tune",
              hay: `${label} ${r.keywords ?? ""}`.replace(/([a-z])([A-Z])/g, "$1 $2").toLowerCase(),
              run: goTo(tabId, s.id, r.id, exp),
            });
          }
          // A custom block's inner controls land on the block.
          for (const t of custom) {
            out.push({
              id: `c:${tabId}.${s.id}.${r.id}.${t}`,
              title: t.replace(/([a-z])([A-Z])/g, "$1 $2"),
              where: `${gl} › ${st}`,
              icon: "tune",
              hay: t.replace(/([a-z])([A-Z])/g, "$1 $2").toLowerCase(),
              run: goTo(tabId, s.id, r.id, exp),
            });
          }
        }
      }
    }
    return out;
  }, [ui, patchUi, engine, store, ctx, config.on, draft, hasModel, sceneFiles, saveScene, openScene, ringWithScene]);

  const words = tokenize(q);
  const results = useMemo(() => {
    if (!words.length) return items.filter((i) => i.id.startsWith("a:") || i.id.startsWith("g:"));
    const scored: { item: Item; score: number }[] = [];
    for (const item of items) {
      if (!words.every((w) => expand(w).some((x) => item.hay.includes(x)))) continue;
      const t = item.title.toLowerCase();
      const first = words[0];
      const score = (t.startsWith(first) ? 0 : t.includes(first) ? 1 : 2) + (item.id.startsWith("r:") || item.id.startsWith("s:") ? 0 : 0.5);
      scored.push({ item, score });
    }
    return scored.sort((a, b) => a.score - b.score).slice(0, 60).map((s) => s.item);
  }, [items, words]);
  const mark = markTerms(q);

  useEffect(() => setAt(0), [q]);
  useEffect(() => {
    listRef.current?.querySelector(`[data-i="${at}"]`)?.scrollIntoView({ block: "nearest" });
  }, [at]);

  const choose = (i: number) => {
    const item = results[i];
    if (!item) return;
    onClose();
    item.run();
  };

  return createPortal(
    <div className="editor-theme fixed inset-0 z-[300] flex items-start justify-center bg-black/20 pt-[12vh]" onMouseDown={onClose}>
      <div
        role="dialog"
        aria-label="Go to a setting or action"
        className="w-[560px] max-w-[calc(100vw-32px)] overflow-hidden rounded-xl border border-ed-border bg-ed-panel shadow-[0_24px_60px_rgba(0,0,0,0.18)]"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex h-12 items-center gap-2.5 border-b border-ed-border px-4">
          <Icon name="search" size={19} className="text-ed-text-3" />
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Go to a setting or action…"
            aria-label="Go to a setting or action"
            aria-controls="palette-list"
            aria-activedescendant={results[at] ? `palette-${at}` : undefined}
            onKeyDown={(e) => {
              if (e.key === "Escape") { e.preventDefault(); onClose(); }
              else if (e.key === "ArrowDown") { e.preventDefault(); setAt((v) => Math.min(results.length - 1, v + 1)); }
              else if (e.key === "ArrowUp") { e.preventDefault(); setAt((v) => Math.max(0, v - 1)); }
              else if (e.key === "Enter") { e.preventDefault(); choose(at); }
            }}
            className="min-w-0 flex-1 bg-transparent text-[15px] text-ed-text outline-none placeholder:text-ed-text-3"
            data-palette-input
            data-own-focus
          />
          <kbd className="rounded border border-ed-border px-1.5 text-[11px] leading-[18px] text-ed-text-3">esc</kbd>
        </div>
        <div id="palette-list" role="listbox" ref={listRef} className="scroll-thin max-h-[52vh] overflow-y-auto py-1.5">
          {results.length === 0 ? (
            <p className="px-4 py-6 text-center text-[13px] text-ed-text-3">Nothing by that name.</p>
          ) : (
            results.map((item, i) => (
              <button
                key={item.id}
                id={`palette-${i}`}
                role="option"
                aria-selected={i === at}
                data-i={i}
                type="button"
                onMouseMove={() => setAt(i)}
                onClick={() => choose(i)}
                className={`flex w-full items-center gap-3 px-4 py-2 text-left ${i === at ? "bg-ed-active" : ""}`}
              >
                <Icon name={item.icon} size={17} className="shrink-0 text-ed-text-3" />
                <span className="min-w-0 flex-1 truncate text-[13px] text-ed-text">
                  <Marked text={item.title} terms={mark} />
                </span>
                <span className="max-w-[45%] shrink-0 truncate text-[12px] text-ed-text-3">{item.where}</span>
              </button>
            ))
          )}
        </div>
        <div className="flex h-9 items-center gap-4 border-t border-ed-border px-4 text-[11px] text-ed-text-3">
          <span>↑↓ to move · Enter to open</span>
          <span className="ml-auto">⌘Z undo · ⇧⌘Z redo · / search</span>
        </div>
      </div>
    </div>,
    document.body,
  );
}
